import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ChargeService } from 'app/core/services/charge.service';
import { DataStorageService } from 'app/core/services/data-storage.service';
import { DeptBranchJobService } from 'app/core/services/dept-branch-job.service';
import { PayrollConceptService } from 'app/core/services/payroll-concept.service';
import { PayrollDefinitionService } from 'app/core/services/payroll-definition.service';
import { SalaryScaleService } from 'app/core/services/salary-scale.service';
import { TodoInitialDataService } from 'app/core/services/todo-initial-data.service';
import { Account } from 'app/shared/models/account';

@Component({
  selector: 'app-alert',
  templateUrl: './alert.component.html',
  styleUrls: ['./alert.component.scss']
})
export class AlertComponent implements OnInit {

  initialData: any;
  alertClass: string = '';
  alertContent: string = '';
  alertVisible: boolean = false;

  tasks: { text: string; checked: boolean }[] = [
    { text: 'Setup Charge', checked: false },
    { text: 'Setup Salary Scale', checked: false },
    { text: 'Setup Payroll Concept', checked: false },
    { text: 'Setup Dep./Branch/Job', checked: false },
    { text: 'Setup Payroll Definition', checked: false }
  ];

  newTask: string = '';
  completedCount: number = 0;
  totalTasks: number = this.tasks.length;
  pendingCount: number = this.totalTasks;
  accountInfo: Account;

  showOverdueAlert = true;
  showDueSoonAlert = true;

  constructor(private dataStorage: DataStorageService,
    private chargeService: ChargeService,
    private salaryScaleService: SalaryScaleService,
    private payrollConceptService: PayrollConceptService,
    private deptBranchJobService: DeptBranchJobService,
    private payrollDefinitionService: PayrollDefinitionService,
    private route: ActivatedRoute,
    private todoInitialDataService: TodoInitialDataService,
    private router: Router
  ) { }

  ngOnInit(): void {
    const account = localStorage.getItem('account');
    if ((account)) {
      this.accountInfo = JSON.parse(account);
    }

    this.initialData = this.dataStorage.getInitialData();

    const configStatus = this.todoInitialDataService.getTodoData();

    if (configStatus) {
      this.updateTaskChecked('Setup Charge', configStatus.charges);
      this.updateTaskChecked('Setup Salary Scale', configStatus.salaryScales);
      this.updateTaskChecked('Setup Payroll Concept', configStatus.payrollConcept);
      this.updateTaskChecked('Setup Dep./Branch/Job', configStatus.deptBranchJob);
      this.updateTaskChecked('Setup Payroll Definition', configStatus.payrollDefinitions);

      this.updateCounts();
    }

    if (this.initialData.status === 'expiring soon') {
      this.alertClass = 'custom-alert bg-warning';
      this.alertContent = 'Your subscription is expiring soon. Please renew to continue services.';
      this.alertVisible = true;
    } else if (this.initialData.status === 'expired') {
      this.alertClass = 'custom-alert bg-danger';
      this.alertContent = 'Your subscription has expired. Please renew to regain access.';
      this.alertVisible = true;
    } else {
      this.alertVisible = false;
    }
  }

  isOverdue(date: string | Date): boolean {
    if (!date) return false;
    const today = new Date();
    const due = new Date(date);
    due.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return due < today;
  }

  isDueSoon(date: string | Date | null | undefined): boolean {
    if (!date) return false;

    const dueDate = new Date(date);
    const today = new Date();

    dueDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    const diffInMs = dueDate.getTime() - today.getTime();
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

    return diffInDays > 0 && diffInDays <= 5;
  }

  private updateTaskChecked(taskText: string, isChecked: boolean) {
    const task = this.tasks.find(t => t.text === taskText);
    if (task && task.checked !== isChecked) {
      task.checked = isChecked;
      this.updateCounts();
    }
  }

  private updateCounts() {
    this.completedCount = this.tasks.filter(task => task.checked).length;
    this.pendingCount = this.totalTasks - this.completedCount;
  }

  getCharges() {
    this.chargeService.getCharges().subscribe({
      next: res => this.updateTaskChecked('Setup Charge', res.length > 0),
      error: err => console.log(err)
    });
  }

  getSalaryScale() {
    this.salaryScaleService.getScales().subscribe({
      next: res => this.updateTaskChecked('Setup Salary Scale', res.length > 0),
      error: err => console.log(err)
    });
  }

  getPayrollConcepts() {
    let catalogsLoaded = false;
    let categoriesLoaded = false;

    this.payrollConceptService.getCatalogs().subscribe({
      next: res => {
        catalogsLoaded = res.length > 0;
        this.updateTaskChecked('Setup Payroll Concept', catalogsLoaded && categoriesLoaded);
      },
      error: err => {
        console.log(err);
        catalogsLoaded = false;
        this.updateTaskChecked('Setup Payroll Concept', false);
      }
    });

    this.payrollConceptService.getCategories().subscribe({
      next: res => {
        categoriesLoaded = res.length > 0;
        this.updateTaskChecked('Setup Payroll Concept', catalogsLoaded && categoriesLoaded);
      },
      error: err => {
        console.log(err);
        categoriesLoaded = false;
        this.updateTaskChecked('Setup Payroll Concept', false);
      }
    });
  }

  getDeptBranchJob() {
    let departmentsLoaded = false;
    let branchesLoaded = false;
    let jobsLoaded = false;

    const updateTaskStatus = () => {
      this.updateTaskChecked('Setup Dep./Branch/Job', departmentsLoaded && branchesLoaded && jobsLoaded);
    };

    this.deptBranchJobService.getDepartments().subscribe({
      next: res => {
        departmentsLoaded = res.length > 0;
        updateTaskStatus();
      },
      error: err => {
        console.log(err);
        departmentsLoaded = false;
        updateTaskStatus();
      }
    });

    this.deptBranchJobService.getBranches().subscribe({
      next: res => {
        branchesLoaded = res.length > 0;
        updateTaskStatus();
      },
      error: err => {
        console.log(err);
        branchesLoaded = false;
        updateTaskStatus();
      }
    });

    this.deptBranchJobService.getJobs().subscribe({
      next: res => {
        jobsLoaded = res.length > 0;
        updateTaskStatus();
      },
      error: err => {
        console.log(err);
        jobsLoaded = false;
        updateTaskStatus();
      }
    });
  }

  getPayrollDefinition() {
    this.payrollDefinitionService.getPayrollDefinitions().subscribe({
      next: res => this.updateTaskChecked('Setup Payroll Definition', res.length > 0),
      error: err => console.log(err)
    });
  }

  navigateToPlan() {
    this.router.navigate(['user-profile']);
  }

  navigateToTodo() {
    this.router.navigate(['todo']);
  }

  navigateToBilling() {
    this.router.navigate(['billing-summary']);
  }
}
